/* Settings (02 §3.7) — the only configuration surface.
 *
 * M4 owns the **Offline** section: the Pyodide pack download with its Wi-Fi
 * recommendation, real size, removal and repair. Sync, Learning and About are
 * here too because every value they need already exists and honestly showing
 * them costs nothing.
 *
 * M5 adds Notifications, including the iOS reality that push exists only in an
 * INSTALLED PWA — so the row explains why it cannot rather than offering a
 * toggle that does nothing. Device & security is named but not faked; that
 * machinery arrives with M6.
 */

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_NEW_CARD_CAP, NEW_CARD_CAP_MAX, NEW_CARD_CAP_MIN } from '@ascent/shared';
import { api, ApiFailure, storeToken, type MeResponse } from '../api';
import { outboxCount } from '../db';
import { formatBytes, installPack, packState, removePack, type PackState } from '../exercise/pack';
import { disablePush, enablePush, readPushState, type PushState } from '../push';

const DEEP_BLOCK_DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

export function SettingsScreen({
  me, lastSyncAt, onBack, onSyncNow,
}: {
  me: MeResponse | null;
  lastSyncAt: string | null;
  onBack: () => void;
  onSyncNow: () => Promise<void>;
}) {
  const [pack, setPack] = useState<PackState | null>(null);
  const [busy, setBusy] = useState<'install' | 'remove' | 'sync' | 'push' | 'rotate' | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [packError, setPackError] = useState<string | null>(null);
  const [pending, setPending] = useState(0);

  const [push, setPush] = useState<PushState | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [rotateNote, setRotateNote] = useState<string | null>(null);
  const [nudgeAt, setNudgeAt] = useState('18:00');
  const [dueNudge, setDueNudge] = useState(true);
  const [digest, setDigest] = useState(true);
  const [cap, setCap] = useState<number>(DEFAULT_NEW_CARD_CAP);
  const [day, setDay] = useState<string>('saturday');
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const refreshPack = useCallback(async () => setPack(await packState()), []);

  useEffect(() => { void refreshPack(); }, [refreshPack]);
  useEffect(() => { void outboxCount().then(setPending); }, []);
  useEffect(() => { void readPushState().then(setPush); }, []);

  useEffect(() => {
    const s = me?.settings ?? {};
    if (typeof s['new_card_cap'] === 'number') setCap(s['new_card_cap']);
    if (typeof s['deep_block_day'] === 'string') setDay(s['deep_block_day']);
    if (typeof s['push_nudge_time'] === 'string') setNudgeAt(s['push_nudge_time']);
    if (typeof s['push_due_nudge'] === 'boolean') setDueNudge(s['push_due_nudge']);
    if (typeof s['push_weekly_digest'] === 'boolean') setDigest(s['push_weekly_digest']);
  }, [me]);

  const install = useCallback(async () => {
    setBusy('install');
    setPackError(null);
    setProgress(0);
    const res = await installPack((done, total) => setProgress(total ? done / total : 0));
    if (!res.ok) setPackError(res.error);
    setProgress(null);
    setBusy(null);
    await refreshPack();
  }, [refreshPack]);

  const remove = useCallback(async () => {
    setBusy('remove');
    await removePack();
    setBusy(null);
    await refreshPack();
  }, [refreshPack]);

  const saveSettings = useCallback(async (patch: Record<string, unknown>) => {
    setSettingsError(null);
    try {
      await api.patchSettings(patch);
      setSavedNote('Saved. It takes effect with tomorrow’s plan.');
      setTimeout(() => setSavedNote(null), 4000);
    } catch (err) {
      setSettingsError(
        err instanceof ApiFailure
          ? err.code === 'OFFLINE'
            ? 'Offline — settings need the server. Try again once connected.'
            : err.message
          : 'Could not save.',
      );
    }
  }, []);

  const pct = pack && pack.totalBytes > 0 ? pack.bytesPresent / pack.totalBytes : 0;

  return (
    <main className="screen">
      <button type="button" className="back" onClick={onBack}>← Progress</button>
      <p className="eyebrow">Settings</p>
      <h1>Settings</h1>

      {/* ---- Sync -------------------------------------------------------- */}
      <div className="card">
        <div className="card__title"><span>Sync</span></div>
        <p style={{ margin: '0 0 0.5rem' }}>
          {lastSyncAt ? `Last sync ${new Date(lastSyncAt).toLocaleString()}` : 'Not synced yet on this device.'}
        </p>
        <p className="hint" style={{ marginTop: 0 }}>
          {pending === 0 ? 'Nothing waiting.' : `${pending} ${pending === 1 ? 'event' : 'events'} waiting to send.`}
        </p>
        <button
          type="button"
          disabled={busy !== null || !navigator.onLine}
          onClick={() => { setBusy('sync'); void onSyncNow().finally(() => { setBusy(null); void outboxCount().then(setPending); }); }}
        >
          {busy === 'sync' ? 'Syncing…' : 'Sync now'}
        </button>
        {!navigator.onLine ? <p className="hint">Offline — everything you do is saved and sends itself later.</p> : null}
      </div>

      {/* ---- Offline pack ------------------------------------------------ */}
      <div className="card">
        <div className="card__title">
          <span>Offline pack</span>
          <span className="card__meta">{pack ? formatBytes(pack.totalBytes) : '—'}</span>
        </div>
        <p style={{ margin: '0 0 0.875rem' }}>
          Python exercises run on this phone, not on a server. The pack is the
          Python runtime itself — install it once and <strong>python</strong> exercises
          work in airplane mode. SQL and reading exercises need no pack at all.
        </p>

        {pack?.installed ? (
          <>
            <p className="badge badge--good">Installed · version {pack.version}</p>
            <div style={{ marginTop: '0.875rem' }}>
              <button type="button" onClick={() => void remove()} disabled={busy !== null}>
                {busy === 'remove' ? 'Removing…' : 'Remove pack'}
              </button>
            </div>
            <p className="hint">
              Removing frees {formatBytes(pack.totalBytes)}. Python exercises then need a
              connection again.
            </p>
          </>
        ) : (
          <>
            {pack && pack.bytesPresent > 0 ? (
              <p className="hint">
                Partly downloaded ({formatBytes(pack.bytesPresent)} of {formatBytes(pack.totalBytes)}).
                Installing again keeps what is already here.
              </p>
            ) : null}
            {progress !== null ? (
              <div className="progressbar" aria-label="Download progress">
                <span className="progressbar__fill" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            ) : null}
            <button type="button" onClick={() => void install()} disabled={busy !== null || !navigator.onLine}>
              {busy === 'install'
                ? `Downloading… ${Math.round((progress ?? 0) * 100)}%`
                : `Download pack (${pack ? formatBytes(pack.totalBytes) : '~13 MB'})`}
            </button>
            <p className="hint">Wi-Fi recommended — this is a real download, not a few kilobytes.</p>
            {!navigator.onLine ? <p className="hint">You are offline; the pack needs a connection once.</p> : null}
          </>
        )}
        {pack && !pack.installed && pack.bytesPresent > 0 ? (
          <div className="progressbar" aria-hidden="true">
            <span className="progressbar__fill" style={{ width: `${Math.round(pct * 100)}%` }} />
          </div>
        ) : null}
        {packError ? <p className="error">{packError}</p> : null}
      </div>

      {/* ---- Learning ---------------------------------------------------- */}
      <div className="card">
        <div className="card__title"><span>Learning</span></div>

        <label htmlFor="cap">New Cards per day</label>
        <input
          id="cap" type="number" inputMode="numeric"
          min={NEW_CARD_CAP_MIN} max={NEW_CARD_CAP_MAX} value={cap}
          onChange={(e) => setCap(Number(e.target.value))}
          onBlur={() => {
            const v = Math.min(NEW_CARD_CAP_MAX, Math.max(NEW_CARD_CAP_MIN, Math.round(cap)));
            setCap(v);
            void saveSettings({ new_card_cap: v });
          }}
        />
        <p className="hint">
          {NEW_CARD_CAP_MIN}–{NEW_CARD_CAP_MAX}, default {DEFAULT_NEW_CARD_CAP}. This caps how many
          new Cards enter the rotation — it never caps reviews that are due.
        </p>

        <label htmlFor="deepday">Weekly deep-block day</label>
        <select
          id="deepday" value={day}
          onChange={(e) => { setDay(e.target.value); void saveSettings({ deep_block_day: e.target.value }); }}
        >
          {DEEP_BLOCK_DAYS.map((d) => (
            <option key={d} value={d}>{d[0]!.toUpperCase() + d.slice(1)}</option>
          ))}
        </select>
        <p className="hint">Also the day the weekly digest fires, once notifications land in M5.</p>

        {savedNote ? <p className="hint">{savedNote}</p> : null}
        {settingsError ? <p className="error">{settingsError}</p> : null}
      </div>

      {/* ---- Notifications ----------------------------------------------- */}
      <div className="card">
        <div className="card__title">
          <span>Notifications</span>
          <span className="card__meta">{push?.status === 'on' ? 'on' : 'off'}</span>
        </div>

        {push === null ? (
          <p className="hint">Checking…</p>
        ) : push.status === 'not_installed' ? (
          <p style={{ margin: 0 }}>
            iOS only allows notifications in an <strong>installed</strong> app. Add ASCENT
            to your Home Screen from the Share menu, open it from there, and this
            turns on.
          </p>
        ) : push.status === 'unsupported' ? (
          <p style={{ margin: 0 }}>{push.reason}</p>
        ) : push.status === 'denied' ? (
          <p style={{ margin: 0 }}>
            Notifications are blocked for ASCENT. iOS asks only once — change it in
            iOS Settings → Notifications → ASCENT.
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 0.875rem' }}>
              One nudge a day at most, only when Cards are actually due and only if
              you have not already done today's work. Quiet from 22:00 to 08:00.
            </p>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy('push');
                void (async () => {
                  if (push.status === 'on') { await disablePush(); }
                  else {
                    const res = await enablePush();
                    if (!res.ok) setPushError(res.error);
                  }
                  setPush(await readPushState());
                  setBusy(null);
                })();
              }}
            >
              {busy === 'push'
                ? 'Working…'
                : push.status === 'on' ? 'Turn notifications off' : 'Turn notifications on'}
            </button>
            {pushError ? <p className="error">{pushError}</p> : null}

            {push.status === 'on' ? (
              <>
                <label htmlFor="nudge">Nudge time</label>
                <input
                  id="nudge" type="time" value={nudgeAt}
                  onChange={(e) => setNudgeAt(e.target.value)}
                  onBlur={() => void saveSettings({ push_nudge_time: nudgeAt })}
                />
                <ul className="kv" style={{ marginTop: '0.75rem' }}>
                  <li>
                    <span>Due-drills nudge</span>
                    <span>
                      <input
                        type="checkbox" checked={dueNudge}
                        onChange={(e) => { setDueNudge(e.target.checked); void saveSettings({ push_due_nudge: e.target.checked }); }}
                      />
                    </span>
                  </li>
                  <li>
                    <span>Weekly digest</span>
                    <span>
                      <input
                        type="checkbox" checked={digest}
                        onChange={(e) => { setDigest(e.target.checked); void saveSettings({ push_weekly_digest: e.target.checked }); }}
                      />
                    </span>
                  </li>
                </ul>
                <p className="hint">
                  Budget and self-check alerts stay on and ignore quiet hours — they
                  guard money and your data, so they are not mine to silence.
                </p>
              </>
            ) : null}
          </>
        )}
      </div>

      {/* ---- About ------------------------------------------------------- */}
      <div className="card">
        <div className="card__title"><span>About</span></div>
        <ul className="kv">
          <li><span>App</span><span>{me?.server.version ?? '—'}</span></li>
          <li><span>API</span><span>v{me?.server.api_version ?? '—'}</span></li>
          <li><span>Content release</span><span>{me?.active_release_id ?? 'none yet'}</span></li>
          <li><span>Device</span><span>{me?.device.name ?? '—'}</span></li>
        </ul>
      </div>

      {/* ---- Device & security ------------------------------------------ */}
      <div className="card">
        <div className="card__title"><span>Device &amp; security</span></div>
        <p style={{ margin: '0 0 0.875rem' }}>
          This phone holds one credential. Rotating replaces it in place — you stay
          signed in, and the old token stops working after a short grace period.
        </p>
        <button
          type="button"
          disabled={busy !== null || !navigator.onLine}
          onClick={() => {
            setBusy('rotate');
            setRotateNote(null);
            void (async () => {
              try {
                const res = await api.rotateToken();
                // Store it BEFORE reporting success: a token we announced but
                // failed to persist would lock the device out at the next call.
                storeToken(res.device_token, localStorage.getItem('ascent.device_id') ?? '');
                setRotateNote('Token rotated. Nothing else to do — you are still signed in.');
              } catch (err) {
                setRotateNote(err instanceof ApiFailure ? err.message : 'Could not rotate the token.');
              } finally {
                setBusy(null);
              }
            })();
          }}
        >
          {busy === 'rotate' ? 'Rotating…' : 'Rotate token'}
        </button>
        {rotateNote ? <p className="hint">{rotateNote}</p> : null}

        <p className="hint" style={{ marginTop: '1rem' }}>
          <strong>Lost or replaced phone?</strong> Enrolment is deliberately not
          self-service: issue a recovery code on the server with{' '}
          <code>ascent-cli issue-enrollment-code --recovery</code>. That revokes this
          device and its notifications before the replacement exists — which is the
          point, and why no button here can do it.
        </p>
      </div>

      <p className="footnote">
        Your learning lives on the server, not on this phone. Rotating a token or
        re-enrolling never touches mastery, scheduling or history.
      </p>
    </main>
  );
}
